<template>
    <div class="google-map-container h-full w-full relative">
        <!-- Loading state -->
        <div v-if="isLoading" class="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10">
            <div class="text-center">
                <div class="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-600 mx-auto mb-4"></div>
                <p class="text-gray-600">Loading map...</p>
            </div>
        </div>

        <!-- Error state -->
        <div v-if="hasError" class="absolute inset-0 bg-white flex items-center justify-center z-10">
            <div class="text-center">
                <div class="bg-danger-100 rounded-full p-4 mb-4 mx-auto w-16 h-16 flex items-center justify-center">
                    <i class="fas fa-exclamation-triangle text-2xl text-danger-600"></i>
                </div>
                <h3 class="text-lg font-medium text-gray-900 mb-2">Map loading failed</h3>
                <p class="text-gray-600 mb-4">{{ errorMessage }}</p>
                <button @click="initializeMap" class="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700">
                    Reload
                </button>
            </div>
        </div>

        <!-- Google Map container - Always render but may be hidden -->
        <div ref="mapContainer" class="h-full w-full" :class="{ 'opacity-0': isLoading || hasError }"></div>

        <!-- Results statistics -->
        <div v-if="!isLoading && !hasError"
            class="absolute top-14 left-2 bg-white rounded-lg px-4 py-2 shadow-lg border border-gray-200 z-10">
            <p class="text-sm text-gray-700">
                <i class="fas fa-map-marker-alt text-accent-600 mr-1"></i>
                Displaying <span class="font-semibold text-primary-600">{{ displayedCentersCount }}</span> medical
                centers
            </p>
        </div>
    </div>
</template>

<script>
import { ref, onMounted, watch, nextTick } from 'vue'

export default {
    name: 'GoogleMapView',
    props: {
        centers: {
            type: Array,
            default: () => []
        },
        filters: {
            type: Object,
            default: () => ({})
        }
    },
    emits: ['markerClick', 'mapReady', 'error'],
    setup(props, { emit }) {
        const mapContainer = ref(null)
        const isLoading = ref(true)
        const hasError = ref(false)
        const errorMessage = ref('')

        let map = null
        let markers = []
        let infoWindow = null
        let geocoder = null

        // Collect geocoded coordinates for batch update
        const geocodedCoordinates = []

        // Calculate displayed medical centers count
        const displayedCentersCount = ref(0)

        // Malaysia center coordinates
        const MALAYSIA_CENTER = { lat: 4.2105, lng: 101.9758 }
        const DEFAULT_ZOOM = 7

        // Wait for Google Maps API to load
        const waitForGoogleMapsAPI = () => {
            return new Promise((resolve, reject) => {
                // If already loaded, return directly
                if (window.google && window.google.maps) {
                    resolve()
                    return
                }

                // Listen for Google Maps API load events
                const handleMapsLoaded = () => {
                    window.removeEventListener('google-maps-loaded', handleMapsLoaded)
                    window.removeEventListener('google-maps-error', handleMapsError)
                    resolve()
                }

                const handleMapsError = (event) => {
                    window.removeEventListener('google-maps-loaded', handleMapsLoaded)
                    window.removeEventListener('google-maps-error', handleMapsError)
                    reject(new Error(event.detail?.message || 'Google Maps API loading failed'))
                }

                window.addEventListener('google-maps-loaded', handleMapsLoaded)
                window.addEventListener('google-maps-error', handleMapsError)

                // Set timeout
                setTimeout(() => {
                    window.removeEventListener('google-maps-loaded', handleMapsLoaded)
                    window.removeEventListener('google-maps-error', handleMapsError)
                    reject(new Error('Google Maps API loading timeout'))
                }, 15000)
            })
        }

        // Initialize map
        const initializeMap = async () => {
            try {
                hasError.value = false

                // Check if Google Maps API is loaded
                if (typeof google === 'undefined') {
                    // Wait for Google Maps API to load
                    await waitForGoogleMapsAPI()
                }

                await nextTick()

                // Check if map container exists
                if (!mapContainer.value) {
                    throw new Error('Map container element not found')
                }



                // Create map instance
                map = new google.maps.Map(mapContainer.value, {
                    center: MALAYSIA_CENTER,
                    zoom: DEFAULT_ZOOM,
                    mapTypeId: google.maps.MapTypeId.ROADMAP,
                    styles: [
                        {
                            featureType: 'poi',
                            elementType: 'labels',
                            stylers: [{ visibility: 'off' }]
                        }
                    ],
                    mapTypeControl: true,
                    zoomControl: true,
                    streetViewControl: false,
                    fullscreenControl: true
                })

                // Create InfoWindow
                infoWindow = new google.maps.InfoWindow()

                // Create Geocoder
                geocoder = new google.maps.Geocoder()

                // Load medical center markers
                await loadCenterMarkers()

                isLoading.value = false
                emit('mapReady', map)

            } catch (error) {
                console.error('Map initialization failed:', error)
                hasError.value = true
                errorMessage.value = error.message || 'Map loading failed, please try again later'
                isLoading.value = false
                emit('error', error)
            }
        }

        // Load medical center markers
        const loadCenterMarkers = async () => {


            if (!map || !props.centers) {
                return
            }

            // Clear existing markers
            clearMarkers()
            markers = []

            // Clear any previous geocoded coordinates
            geocodedCoordinates.length = 0

            displayedCentersCount.value = props.centers.length


            let geocodingCount = 0
            const centersNeedingGeocoding = props.centers.filter(center => !center.coordinates)

            if (centersNeedingGeocoding.length > 0) {

            }

            // Create markers for each medical center
            props.centers.forEach((center, index) => {


                if (center.coordinates) {


                    const marker = new google.maps.Marker({
                        position: center.coordinates,
                        map: map,
                        title: center.name,
                        icon: {
                            url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="30" viewBox="0 0 384 512">
                  <path d="M172.3 501.7C27 291 0 269.4 0 192 0 86 86 0 192 0s192 86 192 192c0 77.4-27 99-172.3 309.7-9.5 13.8-29.9 13.8-39.5 0zM192 272c44.2 0 80-35.8 80-80s-35.8-80-80-80-80 35.8-80 80 35.8 80 80 80z" fill="#FF0000"/>
                </svg>
              `),
                            scaledSize: new google.maps.Size(24, 30),
                            anchor: new google.maps.Point(12, 30)
                        }
                    })



                    // Map marker click event
                    marker.addListener('click', () => {
                        showInfoWindow(marker, center)
                        emit('markerClick', center)
                    })

                    markers.push({ marker, center })
                } else {
                    // 嘗試使用地址進行地理編碼

                    geocodeCenter(center, centersNeedingGeocoding.length)
                }
            })

            // Adjust map viewport to include all markers
            if (markers.length > 0) {
                const bounds = new google.maps.LatLngBounds()
                markers.forEach(({ center }) => {
                    if (center.coordinates) {
                        bounds.extend(center.coordinates)
                    }
                })
                map.fitBounds(bounds)

                // If there's only one marker, set appropriate zoom level
                if (markers.length === 1) {
                    google.maps.event.addListenerOnce(map, 'bounds_changed', () => {
                        if (map.getZoom() > 10) {
                            map.setZoom(10)
                        }
                    })
                }
            }
        }

        // Display info window
        const showInfoWindow = (marker, center) => {
            const servicesHtml = center.services && center.services.length > 0
                ? center.services.map(service => `<span class="service-tag">${service}</span>`).join(' ')
                : '<span class="text-gray-500">No service information</span>'

            // 構建封面圖片HTML
            const coverImageHtml = center.cover_thumb ? `
                <div class="mb-3">
                    <img src="${center.cover_thumb}" alt="${center.name}" class="w-full h-32 object-cover rounded-lg shadow-sm">
                </div>
            ` : ''

            // 構建完整地址信息
            const addressParts = []
            if (center.address) addressParts.push(center.address)
            if (center.city) addressParts.push(center.city)
            if (center.state && center.state !== center.city) addressParts.push(center.state)
            if (center.country && center.country !== 'Malaysia') addressParts.push(center.country)
            const fullAddress = addressParts.join(', ')

            const content = `
        <div class="info-window max-w-sm">
          ${coverImageHtml}
          <div class="p-4">
            <div class="flex items-start">
              <div class="bg-accent-600 rounded-full p-2 mr-3 flex-shrink-0">
                <i class="fas fa-hospital text-white text-xs"></i>
              </div>
              <div class="flex-1">
                <h3 class="font-semibold text-gray-900 mb-1 text-sm">${center.name}</h3>
                ${fullAddress ? `
                  <div class="text-xs text-gray-600 mb-2">
                    <i class="fas fa-map-marker-alt text-primary-600 mr-1"></i>
                    <span class="break-words">${fullAddress}</span>
                  </div>
                ` : ''}

                ${center.phone ? `
                  <div class="text-xs text-gray-600 mb-2">
                    <i class="fas fa-phone text-secondary-600 mr-1"></i>
                    ${center.phone.replace(/\n/g, '<br>')}
                  </div>
                ` : ''}
                ${center.email ? `
                  <div class="text-xs text-gray-600 mb-2">
                    <i class="fas fa-envelope text-secondary-600 mr-1"></i>
                    <a href="mailto:${center.email}" class="text-secondary-600 hover:text-secondary-700 hover:underline">
                      ${center.email}
                    </a>
                  </div>
                ` : ''}
                <div class="mb-3">
                  <div class="text-xs font-medium text-gray-700 mb-1">Services:</div>
                  <div class="flex flex-wrap gap-1 mb-2">
                    ${servicesHtml}
                  </div>
                  ${center.website && center.website !== 'Not specified' ? `
                    <a href="${center.website}" target="_blank" 
                       class="inline-flex items-center px-3 py-1.5 bg-primary-600 hover:bg-primary-700 text-white text-xs font-medium rounded-full transition-colors shadow-sm">
                      <i class="fas fa-external-link-alt mr-1.5"></i>
                      Visit Website
                    </a>
                  ` : ''}
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <style>
          .info-window {
            background: white;
            border-radius: 8px;
            overflow: hidden;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          }
          .info-window .service-tag {
            display: inline-block;
            background: #e5f7f4;
            color: #0d9488;
            padding: 2px 6px;
            border-radius: 4px;
            font-size: 9px;
            font-weight: 500;
            margin-right: 3px;
            margin-bottom: 2px;
          }
          .info-window a {
            text-decoration: none;
          }
          .info-window a:hover {
            text-decoration: underline;
          }
          .info-window img {
            max-width: 100%;
            height: auto;
          }
        </style>
      `

            infoWindow.setContent(content)
            infoWindow.open(map, marker)
        }

        // Geocode center using address information
        const geocodeCenter = (center, totalGeocodingCount) => {
            if (!geocoder) {
                console.warn('Geocoder not initialized')
                return
            }

            // Build address string from available information
            const addressParts = []

            // 優先使用完整地址，如果沒有則用名稱和城市
            if (center.address && center.address.length > 10) {
                addressParts.push(center.address)
            } else {
                if (center.name) addressParts.push(center.name)
                if (center.address) addressParts.push(center.address)
                if (center.city) addressParts.push(center.city)
                if (center.state && center.state !== center.city) addressParts.push(center.state)
            }

            // 總是添加國家以提高地理編碼準確性
            if (center.country) addressParts.push(center.country)

            const addressString = addressParts.join(', ')



            geocoder.geocode({ address: addressString }, (results, status) => {
                if (status === 'OK' && results[0]) {
                    const location = results[0].geometry.location
                    const lat = location.lat()
                    const lng = location.lng()



                    // Collect coordinates for batch update
                    geocodedCoordinates.push({
                        id: center.id,
                        latitude: lat,
                        longitude: lng
                    })

                    // Check if all geocoding is complete
                    if (geocodedCoordinates.length === totalGeocodingCount) {

                        // Use setTimeout to ensure all markers are created first
                        setTimeout(() => {
                            saveGeocodedCoordinates()
                        }, 1000)
                    }

                    // Create marker with geocoded position
                    const marker = new google.maps.Marker({
                        position: { lat: lat, lng: lng },
                        map: map,
                        title: center.name,
                        icon: {
                            url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="30" viewBox="0 0 384 512">
                  <path d="M172.3 501.7C27 291 0 269.4 0 192 0 86 86 0 192 0s192 86 192 192c0 77.4-27 99-172.3 309.7-9.5 13.8-29.9 13.8-39.5 0zM192 272c44.2 0 80-35.8 80-80s-35.8-80-80-80-80 35.8-80 80 35.8 80 80 80z" fill="#FF0000"/>
                </svg>
              `),
                            scaledSize: new google.maps.Size(24, 30),
                            anchor: new google.maps.Point(12, 30)
                        }
                    })

                    // Add marker click event
                    marker.addListener('click', () => {
                        showInfoWindow(marker, center)
                        emit('markerClick', center)
                    })

                    markers.push({ marker, center })

                    // Update displayed count
                    displayedCentersCount.value = markers.length

                } else {
                    console.warn(`Geocoding failed for ${center.name}: ${status}`)
                }
            })
        }

        // Batch save all geocoded coordinates to database
        const saveGeocodedCoordinates = async () => {
            if (geocodedCoordinates.length === 0) {
                return
            }



            try {
                const response = await fetch('/api/admin/private/centers/batch-coordinates', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json',
                        'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
                    },
                    credentials: 'same-origin',
                    body: JSON.stringify({
                        centers: geocodedCoordinates
                    })
                })

                if (response.ok) {
                    const result = await response.json()

                    // Clear the collected coordinates
                    geocodedCoordinates.length = 0
                } else {
                    console.warn('Failed to batch save coordinates:', response.statusText)
                }
            } catch (error) {
                console.warn('Error batch saving coordinates:', error)
            }
        }

        // Clear all markers
        const clearMarkers = () => {
            markers.forEach(({ marker }) => {
                marker.setMap(null)
            })
            markers = []
        }

        // Watch for medical centers data changes
        watch(() => props.centers, async () => {
            if (map) {
                await loadCenterMarkers()
            }
        }, { deep: true })

        // Initialize map when component is mounted, with delay to ensure DOM is ready
        onMounted(async () => {
            // Wait for DOM to be fully ready
            await nextTick()

            // Wait longer to ensure map container is rendered
            setTimeout(() => {

                if (mapContainer.value) {

                }
                initializeMap()
            }, 500)
        })

        return {
            mapContainer,
            isLoading,
            hasError,
            errorMessage,
            displayedCentersCount,
            initializeMap
        }
    }
}
</script>

<style scoped>
.google-map {
    position: relative;
}

/* Ensure map container has correct dimensions */
.google-map>div {
    min-height: 400px;
}

/* Loading animation */
@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

.animate-spin {
    animation: spin 1s linear infinite;
}
</style>